/**
 * OverviewTab Component (T100 - US4)
 * Admin dashboard overview with platform metrics
 * 
 * TEMPORARILY DISABLED - Metrics endpoints causing server performance issues
 * See: docs/bugfixes/001-admin-metrics-performance/
 * 
 * Original features (disabled):
 * - 4 metric cards: Total Providers, Active Users, Pending Reviews, Platform Revenue
 * - Recent Sign-ups list (last 3 registrations)
 * - Platform Health placeholder
 */

import { Construction } from 'lucide-react';

// =============================================================================
// TEMPORARILY DISABLED IMPORTS - DO NOT REMOVE
// These will be re-enabled once performance issues are resolved
// =============================================================================
// import { Building, Users, AlertCircle, DollarSign, Clock } from 'lucide-react';
// import { useAdminMetrics } from '../hooks/useAdminMetrics';
// import { usePendingProvidersCount } from '../hooks/useAdminProviders';
// import { MetricCard } from './MetricCard';
// import { RecentSignups } from './RecentSignups';
// import { PlatformHealthPlaceholder } from './PlatformHealthPlaceholder';
// import { DashboardSkeleton } from './DashboardSkeleton';

export function OverviewTab() {
  // =============================================================================
  // TEMPORARILY DISABLED - Metrics API calls causing server crashes
  // =============================================================================
  // const { data: metrics, isLoading, error, refetch } = useAdminMetrics();
  // const { data: pendingCount, isLoading: isLoadingPending } = usePendingProvidersCount();

  // =============================================================================
  // TEMPORARY: Show "Coming Soon" message while metrics are disabled
  // =============================================================================
  return (
    <div data-testid="overview-tab" className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
        <p className="text-gray-600 mt-1">
          Platform metrics and key statistics
        </p>
      </div>

      {/* Temporarily Disabled Message */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
        <Construction className="h-16 w-16 text-amber-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-amber-900 mb-2">
          Temporarily Disabled
        </h3>
        <p className="text-amber-700 max-w-md mx-auto">
          The Overview dashboard is currently undergoing performance improvements. 
          This feature will be available again soon.
        </p>
        <p className="text-amber-600 text-sm mt-4">
          In the meantime, you can use the other tabs to manage providers, users, and resources.
        </p>
      </div>
    </div>
  );

  // =============================================================================
  // ORIGINAL IMPLEMENTATION - TEMPORARILY DISABLED
  // Uncomment below and remove "Coming Soon" section above to re-enable
  // =============================================================================
  /*
  // Show loading skeleton while fetching data
  if (isLoading) {
    return <DashboardSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <div data-testid="overview-tab" className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
          <p className="text-gray-600 mt-1">
            Platform metrics and key statistics
          </p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Failed to load metrics
          </h3>
          <p className="text-red-700 mb-4">
            {error.message || 'Unable to fetch platform metrics'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show metrics dashboard
  return (
    <div data-testid="overview-tab" className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
        <p className="text-gray-600 mt-1">
          Platform metrics and key statistics
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Providers"
          value={metrics?.metrics?.totalProviders?.value ?? 'N/A'}
          growth={formatGrowth(metrics?.metrics?.totalProviders?.growth)}
          icon={Building}
        />
        
        <MetricCard
          title="Active Users"
          value={metrics?.metrics?.activeUsers?.value ?? 'N/A'}
          growth={formatGrowth(metrics?.metrics?.activeUsers?.growth)}
          icon={Users}
        />
        
        <MetricCard
          title="Pending Reviews"
          value={metrics?.metrics?.pendingReviews?.value ?? 'N/A'}
          growth={formatGrowth(metrics?.metrics?.pendingReviews?.growth)}
          icon={AlertCircle}
        />
        
        <MetricCard
          title="Platform Revenue"
          value={formatCurrency(metrics?.metrics?.platformRevenue?.value ?? 0)}
          growth={formatGrowth(metrics?.metrics?.platformRevenue?.growth)}
          icon={DollarSign}
        />
      </div>

      {!isLoadingPending && pendingCount !== undefined && pendingCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-yellow-900 mb-1">
                Pending Provider Applications
              </h3>
              <p className="text-sm text-yellow-800">
                <span className="font-bold">{pendingCount}</span>{' '}
                {pendingCount === 1 ? 'provider' : 'providers'} waiting for review.{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    const providersTab = document.querySelector('[data-tab="providers"]') as HTMLElement;
                    if (providersTab) providersTab.click();
                  }}
                  className="font-medium underline hover:text-yellow-900"
                >
                  Review now
                </a>
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-yellow-600 text-white text-sm font-bold">
                {pendingCount}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentSignups signups={metrics?.recentSignups ?? []} />
        <PlatformHealthPlaceholder />
      </div>
    </div>
  );
  */
}

// =============================================================================
// TEMPORARILY DISABLED UTILITY FUNCTIONS
// =============================================================================
/*
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatGrowth(growth: number | undefined): string | undefined {
  if (growth === undefined || growth === null) {
    return undefined;
  }
  
  if (growth === 0) {
    return undefined;
  }
  
  const sign = growth > 0 ? '+' : '';
  return `${sign}${growth.toFixed(1)}%`;
}
*/
